import { useCallback, useEffect, useState } from 'react';

import { useEditHoF, useUploadHoFPhoto } from 'actions/hall-of-fame';

import { editableFieldsFor } from '../constants';
import { MAX_PHOTO_BYTES, compressForUpload } from '../photo';

const recordSnapshot = row => JSON.stringify((row && row.record) || {});

// The three paragraphs a built card carries: the student's story, the exam's explainer,
// and the sign-off. Offered empty so there is somewhere to write before a card exists.
const BLANK_NARRATIVE = ['', '', ''];

// A row with no card still has copy to edit. Assembly parks on missing consent and leaves
// `record` null, and no row in either moderator queue has consent — so without this the
// drawer offers nothing to write on the whole queue. What was drafted earlier comes back
// as `record_override`; seeding from it is what lets a moderator see and revise it rather
// than type it again.
const seedForEditing = row => {
  if (!row || row.record) return row;
  const drafted = row.record_override || {};
  return {
    ...row,
    record: { ...drafted, narrative: drafted.narrative || BLANK_NARRATIVE },
  };
};

// The record open in the review panel, and everything that edits it. Holds the unsaved
// record itself, so closing has to ask before discarding.
const useRecordEditor = ({ toast, confirmThen, refresh, reportOutcome }) => {
  const [editing, setEditing] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [photoError, setPhotoError] = useState(null);
  // Whether a card actually exists, kept apart from `editing.record` because that now
  // carries a working copy for rows that have none. Reading `!!editing.record` for this
  // made a seeded draft look like an assembled card, and the panel offered the card
  // editor on rows whose card had never been built.
  const [hasCard, setHasCard] = useState(false);

  const close = useCallback(() => {
    setEditing(null);
    setBaseline(null);
    setPhotoError(null);
  }, []);

  const { patch: saveEdit, isProcessing: saving } = useEditHoF({
    onComplete: reportOutcome('Save', () => {
      close();
      refresh();
    }),
  });
  const { upload: uploadPhoto, isProcessing: uploadingPhoto } = useUploadHoFPhoto();

  const isDirty = !!editing && baseline !== null && recordSnapshot(editing) !== baseline;

  const open = useCallback(row => {
    const seeded = seedForEditing(row);
    setEditing(seeded);
    setBaseline(recordSnapshot(seeded));
    setHasCard(!!(row && row.record));
    // Otherwise a failure on one record's photo still shows over the next record's.
    setPhotoError(null);
  }, []);

  const requestClose = useCallback(() => {
    if (!isDirty) {
      close();
      return;
    }
    confirmThen(
      'Discard unsaved changes?',
      'Your edits to this record have not been saved.',
      close,
    );
  }, [isDirty, confirmThen, close]);

  // Escape goes through `requestClose`, not `close`, so it prompts over unsaved edits
  // rather than discarding them silently.
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editing, requestClose]);

  const pickPhoto = async (file, event) => {
    if (event && event.target) event.target.value = ''; // allow re-picking the same file
    if (!file || !editing) return;
    setPhotoError(null);
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError('That photo is over 5 MB. Please choose a smaller JPG or PNG.');
      return;
    }
    // nginx rejects bodies over 1 MB and a phone photo is usually 2-5 MB, so without the
    // downscale the common case 413s. Falls back to the original file if it fails.
    const result = await uploadPhoto(editing.id, await compressForUpload(file));
    if (!result.ok) {
      setPhotoError(result.message);
      return;
    }
    // The response is the updated record, so re-open on it rather than refetching and
    // hunting for the row. Re-baselined, so a replaced photo is not an unsaved edit.
    setEditing(result.data);
    setBaseline(recordSnapshot(result.data));
    setHasCard(!!result.data.record);
    toast('Photo updated');
    refresh();
  };

  const setField = (field, value) =>
    setEditing(e => ({ ...e, record: { ...e.record, [field]: value } }));

  const setNarrativeParagraph = (index, value) =>
    setEditing(e => {
      const narrative = [...(e.record.narrative || [])];
      narrative[index] = value;
      return { ...e, record: { ...e.record, narrative } };
    });

  const save = () => {
    const record = editing.record || {};
    const payload = {};
    // Present-only, not every field: a row with no card has nothing under most of these,
    // and sending them would write blanks into the draft the build later folds in.
    // Matches the panel: a HoF row never shows `storyUrl`, so it must not send one --
    // the service refuses it, and the save fails on a field never offered.
    editableFieldsFor(editing.tier).forEach(f => {
      if (f in record) payload[f] = record[f];
    });
    // The blank three paragraphs are scaffolding to type into, not copy. Sending them
    // untouched would store an empty story and count as an edit.
    const narrative = record.narrative || [];
    if (narrative.some(p => String(p || '').trim())) payload.narrative = narrative;
    saveEdit(editing.id, payload);
  };

  return {
    editing,
    hasCard,
    isDirty,
    photoError,
    saving,
    uploadingPhoto,
    open,
    close,
    requestClose,
    pickPhoto,
    setField,
    setNarrativeParagraph,
    save,
  };
};

export default useRecordEditor;
